
import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DiagnosticsService } from '../../../../services/diagnostics.service';
import { DiagnosticTestCardComponent } from '../diagnostic-test-card/diagnostic-test-card.component';

@Component({
  selector: 'app-logic-verification',
  standalone: true,
  imports: [CommonModule, DiagnosticTestCardComponent],
  templateUrl: './logic-verification.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LogicVerificationComponent {
  private diagnosticsService = inject(DiagnosticsService);

  // Results Signals
  billingTestResult = signal<any>(null);
  paymentTestResult = signal<any>(null);
  partnerTestResult = signal<any>(null);
  inventoryTestResult = signal<any>(null);
  medicalTestResult = signal<any>(null);
  dispensingTestResult = signal<any>(null);
  financialSafetyResult = signal<any>(null);
  referralBillingResult = signal<any>(null);
  registrationTestResult = signal<any>(null);
  customerTestResult = signal<any>(null);
  auditorTestResult = signal<any>(null);
  clearingLedgerResult = signal<any>(null);
  queueTestResult = signal<any>(null);
  queueLoadResult = signal<any>(null);

  // Loading States
  isRunningLogicTest = signal(false); // Billing
  isRunningPaymentTest = signal(false);
  isRunningPartnerTest = signal(false);
  isRunningInventoryTest = signal(false);
  isRunningMedicalTest = signal(false);
  isRunningDispensingTest = signal(false);
  isRunningFinancialSafetyTest = signal(false);
  isRunningReferralBillingTest = signal(false);
  isRunningRegistrationTest = signal(false);
  isRunningCustomerTest = signal(false);
  isRunningAuditorTest = signal(false);
  isRunningClearingTest = signal(false);
  isRunningQueueTest = signal(false);
  isRunningQueueLoad = signal(false);

  // --- Test Functions ---

  async runBillingTest() {
      this.isRunningLogicTest.set(true);
      this.billingTestResult.set(null);
      try {
          const result = await this.diagnosticsService.verifyBillingLogic();
          this.billingTestResult.set(result);
      } catch (e: any) {
          this.billingTestResult.set({ status: 'ERROR', message: e.message });
      } finally {
          this.isRunningLogicTest.set(false);
      }
  }

  async runPaymentTest() {
      this.isRunningPaymentTest.set(true);
      this.paymentTestResult.set(null);
      try {
          const result = await this.diagnosticsService.verifyPaymentLogic();
          this.paymentTestResult.set(result);
      } catch (e: any) {
          this.paymentTestResult.set({ status: 'ERROR', message: e.message });
      } finally {
          this.isRunningPaymentTest.set(false);
      }
  }

  async runPartnerTest() {
      this.isRunningPartnerTest.set(true);
      this.partnerTestResult.set(null);
      try {
          const result = await this.diagnosticsService.verifyPartnerLogic();
          this.partnerTestResult.set(result);
      } catch (e: any) {
          this.partnerTestResult.set({ status: 'ERROR', message: e.message });
      } finally {
          this.isRunningPartnerTest.set(false);
      }
  }
  
  async runInventoryTest() {
      this.isRunningInventoryTest.set(true);
      this.inventoryTestResult.set(null);
      try {
          const result = await this.diagnosticsService.verifyInventoryLogic();
          this.inventoryTestResult.set(result);
      } catch (e: any) {
          this.inventoryTestResult.set({ status: 'ERROR', message: e.message });
      } finally {
          this.isRunningInventoryTest.set(false);
      }
  }
  
  async runMedicalTest() {
      this.isRunningMedicalTest.set(true);
      this.medicalTestResult.set(null);
      try {
          const result = await this.diagnosticsService.verifyMedicalLogic();
          this.medicalTestResult.set(result);
      } catch (e: any) {
          this.medicalTestResult.set({ status: 'ERROR', message: e.message });
      } finally {
          this.isRunningMedicalTest.set(false);
      }
  }
  
  async runDispensingTest() {
      this.isRunningDispensingTest.set(true);
      this.dispensingTestResult.set(null);
      try {
          const result = await this.diagnosticsService.verifyDispensingLogic();
          this.dispensingTestResult.set(result);
      } catch (e: any) {
          this.dispensingTestResult.set({ status: 'ERROR', message: e.message });
      } finally {
          this.isRunningDispensingTest.set(false);
      }
  }
  
  async runFinancialSafetyTest() {
      this.isRunningFinancialSafetyTest.set(true);
      this.financialSafetyResult.set(null);
      try {
          const result = await this.diagnosticsService.verifyFinancialSafety();
          this.financialSafetyResult.set(result);
      } catch (e: any) {
          this.financialSafetyResult.set({ status: 'ERROR', message: e.message });
      } finally {
          this.isRunningFinancialSafetyTest.set(false);
      }
  }
  
  async runReferralBillingTest() {
      this.isRunningReferralBillingTest.set(true);
      this.referralBillingResult.set(null);
      try {
          const result = await this.diagnosticsService.verifyReferralBilling();
          this.referralBillingResult.set(result);
      } catch (e: any) {
          this.referralBillingResult.set({ status: 'ERROR', message: e.message });
      } finally {
          this.isRunningReferralBillingTest.set(false);
      }
  }

  async runRegistrationTest() {
      this.isRunningRegistrationTest.set(true);
      this.registrationTestResult.set(null);
      try {
          const result = await this.diagnosticsService.verifyRegistrationLogic();
          this.registrationTestResult.set(result);
      } catch (e: any) {
          this.registrationTestResult.set({ status: 'ERROR', message: e.message });
      } finally {
          this.isRunningRegistrationTest.set(false);
      }
  }

  async runCustomerTest() {
      this.isRunningCustomerTest.set(true);
      this.customerTestResult.set(null);
      try {
          const result = await this.diagnosticsService.verifyCustomerLogic();
          this.customerTestResult.set(result);
      } catch (e: any) {
          this.customerTestResult.set({ status: 'ERROR', message: e.message });
      } finally {
          this.isRunningCustomerTest.set(false);
      }
  }
  
  async runAuditorTest() {
      this.isRunningAuditorTest.set(true);
      this.auditorTestResult.set(null);
      try {
          const result = await this.diagnosticsService.verifyAuditorPermissions();
          this.auditorTestResult.set(result);
      } catch (e: any) {
          this.auditorTestResult.set({ status: 'ERROR', message: e.message });
      } finally {
          this.isRunningAuditorTest.set(false);
      }
  }
  
  async runClearingTest() {
      this.isRunningClearingTest.set(true);
      this.clearingLedgerResult.set(null);
      try {
          const result = await this.diagnosticsService.verifyClearingLedger();
          this.clearingLedgerResult.set(result);
      } catch (e: any) {
          this.clearingLedgerResult.set({ status: 'ERROR', message: e.message });
      } finally {
          this.isRunningClearingTest.set(false);
      }
  }
  
  async runQueueTest() {
      this.isRunningQueueTest.set(true);
      this.queueTestResult.set(null);
      try {
          const result = await this.diagnosticsService.verifyQueueLogic();
          this.queueTestResult.set(result);
      } catch (e: any) {
          this.queueTestResult.set({ status: 'ERROR', message: e.message });
      } finally {
          this.isRunningQueueTest.set(false);
      }
  }

  async runQueueLoadTest() {
      this.isRunningQueueLoad.set(true);
      this.queueLoadResult.set(null);
      const startTime = performance.now();
      try {
          const result = await this.diagnosticsService.verifyQueueLoad();
          const clientDuration = performance.now() - startTime;
          
          // Append client-side latency info
          if (result && result.details) {
              result.details.client_latency_ms = Math.round(clientDuration);
          }
          
          this.queueLoadResult.set(result);
      } catch (e: any) {
          this.queueLoadResult.set({ status: 'ERROR', message: e.message });
      } finally {
          this.isRunningQueueLoad.set(false);
      }
  }
}
